import { useState } from "react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTranslation } from "@/lib/translation-utils";
import { isRTL, getDirection } from "@/lib/translation-utils";
import { cn } from "@/lib/utils";

type IceBreakersProps = {
  onSelect: (message: string) => void;
  trigger?: React.ReactNode;
};

// Use function to get translated ice breakers to ensure they update when language changes
const getIceBreakers = (t: (key: string) => string): string[] => [
  t("What are the key employee rights under Saudi labor law? Can you provide a simple overview?"),
  t("What are the employer's obligations regarding work hours, overtime, and leave in Saudi Arabia?"),
  t("How does Saudi labor law handle termination of employment and end-of-service benefits?")
];

export function IceBreakers({ onSelect, trigger }: IceBreakersProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation(); // Add useTranslation hook to make component re-render on language change
  
  // Get translated ice breakers when the component renders or language changes
  const iceBreakers = getIceBreakers(t);
  
  // Get current text direction (rtl or ltr)
  const isRtl = isRTL();
  const direction = getDirection();

  const handleSelect = (message: string) => {
    onSelect(message);
    setIsOpen(false);
  };

  const dialogId = "ice-breaker-description";

  // Animation direction should be reversed for RTL languages
  const animationDirection = isRtl ? 10 : -10;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
          >
            <Sparkles className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]" dir={direction}>
        <DialogHeader className="text-center">
          <DialogTitle className="text-lg font-semibold text-foreground text-center">
            {t("Message Suggestions")}
          </DialogTitle>
          <DialogDescription id={dialogId} className="text-sm text-muted-foreground text-center">
            {t("Select a question to quickly start a conversation about Saudi labor laws.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-4">
          {iceBreakers.map((message: string, index: number) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: animationDirection }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Button
                variant="ghost"
                className={cn(
                  "w-full text-sm p-4 h-auto hover:bg-primary/10 hover:text-foreground border border-transparent hover:border-primary/20 transition-colors whitespace-normal",
                  isRtl ? "text-right justify-end" : "text-left justify-start"
                )}
                onClick={() => handleSelect(message)}
                dir={direction}
              >
                {message}
              </Button>
            </motion.div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}